import { useEffect, useRef, useState } from 'react';
import type { SlotDemoScene } from '@goodtime/engine';
import {
  friendsJoined,
  lampsLit,
  type FriendId,
  type FriendInfo,
  type LampGameInfo,
  type RingCellKind,
  type RoadState,
} from '@goodtime/shared';

/** Someone to introduce: a friend who has joined, or a game a star lit. */
type Arrival = { friend: FriendInfo } | { game: LampGameInfo };

const arrivalKey = (a: Arrival) => ('friend' in a ? `friend:${a.friend.id}` : `lamp:${a.game.lamp}`);

/**
 * Watches the road for lamps lighting while the slot is open and has the
 * scene introduce each in turn, never over a run or another dialog: on a
 * road, a friend joining (their lamp goes from dark to lit); on a Wheel Run
 * slot, a new game a star lit. Opening the slot with lamps already lit
 * introduces nobody. A lamp whose first game is free can start it there, or
 * later from the lamp.
 */
export function useFriendArrivals({
  road,
  themeId,
  getScene,
  paused,
  onFreePlay,
  onLampFreePlay,
}: {
  road: RoadState | null;
  /** The slot the road is on: a Wheel Run's games are named after it. */
  themeId: string | null;
  getScene: () => SlotDemoScene | null;
  paused: boolean;
  onFreePlay: (friend: FriendId) => void;
  onLampFreePlay: (lamp: RingCellKind) => void;
}): void {
  const last = useRef<RoadState | null>(null);
  const [queue, setQueue] = useState<Arrival[]>([]);
  const showing = useRef(false);
  /** Counts the slot's visits: a card from an earlier visit is done with. */
  const visit = useRef(0);
  const latest = useRef({ road, onFreePlay, onLampFreePlay, getScene });
  latest.current = { road, onFreePlay, onLampFreePlay, getScene };

  // A slot's cards are its own: one still waiting, or still up as the slot
  // closes, isn't carried to the next slot (where its free play would start
  // that slot's game).
  useEffect(() => {
    visit.current += 1;
    showing.current = false;
    setQueue((q) => (q.length ? [] : q));
  }, [themeId]);

  // A lit lamp whose first game is still free offers it when tapped.
  useEffect(() => {
    const scene = getScene();
    if (!scene) return;
    scene.onFriendFreePlay = (friend) => latest.current.onFreePlay(friend);
    scene.onLampFreePlay = (lamp) => latest.current.onLampFreePlay(lamp);
  });

  useEffect(() => {
    const before = last.current;
    last.current = road;
    const arrived: Arrival[] = [
      ...friendsJoined(before, road).map((friend) => ({ friend })),
      ...(themeId ? lampsLit(themeId, before, road) : []).map((game) => ({ game })),
    ];
    if (arrived.length) {
      setQueue((q) => [...q, ...arrived.filter((a) => !q.some((o) => arrivalKey(o) === arrivalKey(a)))]);
    }
  }, [road, themeId]);

  useEffect(() => {
    const next = queue[0];
    const scene = latest.current.getScene();
    if (!next || paused || showing.current || !scene) return;
    showing.current = true;
    const lamps = latest.current.road?.lamps ?? [];
    const shownIn = visit.current;
    const done = (choice: string, play: () => void) => {
      // The slot changed while it was up: that visit's cards have gone.
      if (visit.current !== shownIn) return;
      showing.current = false;
      setQueue((q) => q.slice(1));
      if (choice === 'play') play();
    };
    if ('friend' in next) {
      const friend = next.friend;
      const freePlay = lamps.some((l) => l.friend === friend.id && l.lit && l.freePlay);
      void scene.showFriendJoin(friend, freePlay).then((choice) => done(choice, () => latest.current.onFreePlay(friend.id)));
    } else {
      const game = next.game;
      const freePlay = lamps.some((l) => l.id === game.lamp && l.lit && l.freePlay);
      void scene.showLampLit(game, freePlay).then((choice) => done(choice, () => latest.current.onLampFreePlay(game.lamp)));
    }
  }, [queue, paused]);
}
